import { useEffect, useState } from 'react';

import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { ScrollArea } from '@/components/ui/scroll-area';
import { useOrientation } from '@/hooks/use-orientation';
import { InsightsSummary } from '@/components/home/insights-summary';
import { SmartSuggestionsPanel } from '@/components/home/smart-suggestions-panel';
import { LiveActivityFeed } from '@/components/home/live-activity-feed';
import { LoyaltyRevenueEngine } from '@/components/home/loyalty-revenue-engine';
import { CampaignCommandCenter } from '@/components/home/campaign-command-center';
import { observer } from 'mobx-react-lite';

const HomeDevelopment = () => {
  // In a real app, this would be fetched from an API
  const liveChatEnabled = true;
  const orientation = useOrientation();
  const [gridClass, setGridClass] = useState('');

  // Update grid layout based on orientation and screen width
  useEffect(() => {
    const updateGridClass = () => {
      const width = window.innerWidth;
      const isMobile = width < 768;

      if (isMobile && orientation === 'landscape') {
        setGridClass('grid-cols-1 sm:grid-cols-2');
      } else if (width >= 1024) {
        setGridClass('grid-cols-1 md:grid-cols-2 lg:grid-cols-3');
      } else if (width >= 768) {
        setGridClass('grid-cols-1 md:grid-cols-2');
      } else {
        setGridClass('grid-cols-1');
      }
    };

    updateGridClass();
    window.addEventListener('resize', updateGridClass);
    return () => window.removeEventListener('resize', updateGridClass);
  }, [orientation]);

  return (
    <div
      className={`grid gap-6 ${gridClass} transition-all duration-300 ease-in-out md:p-[24px] py-[10px] mb-14`}
    >
      {/* First row: 3 cards side by side */}
      <div className='col-span-1 md:col-span-1'>
        <InsightsSummary />
      </div>
      <div className='col-span-1 md:col-span-1'>
        <SmartSuggestionsPanel />
      </div>
      <div className='col-span-1 md:col-span-1'>
        <LiveActivityFeed />
      </div>

      {/* Loyalty Revenue Engine */}
      <div className='col-span-full'>
        <LoyaltyRevenueEngine />
      </div>

      {/* Campaign Command Center */}
      <div className='col-span-full'>
        <CampaignCommandCenter />
      </div>

      {/* Last row: 3 cards with consistent styling */}
      <div className='col-span-1 md:col-span-1'>
        <Card className='h-full shadow-none'>
          <CardHeader>
            <CardTitle className='text-lg font-semibold'>
              Top Rewards Redeemed
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ScrollArea className='h-[220px] pr-4'>
              <div className='space-y-4'>
                <div className='flex items-center justify-between'>
                  <div>
                    <div className='font-medium'>20% Off Edibles</div>
                    <div className='text-md text-muted-foreground'>
                      314 redemptions
                    </div>
                  </div>
                  <div className='font-medium'>$2,826</div>
                </div>
                <div className='flex items-center justify-between'>
                  <div>
                    <div className='font-medium'>BOGO Vape</div>
                    <div className='text-md text-muted-foreground'>
                      276 redemptions
                    </div>
                  </div>
                  <div className='font-medium'>$4,140</div>
                </div>
                <div className='flex items-center justify-between'>
                  <div>
                    <div className='font-medium'>First-Time 15% Off</div>
                    <div className='text-md text-muted-foreground'>
                      198 redemptions
                    </div>
                  </div>
                  <div className='font-medium'>$1,782</div>
                </div>
                <div className='flex items-center justify-between'>
                  <div>
                    <div className='font-medium'>$10 Birthday Reward</div>
                    <div className='text-md text-muted-foreground'>
                      187 redemptions
                    </div>
                  </div>
                  <div className='font-medium'>$1,870</div>
                </div>
                <div className='flex items-center justify-between'>
                  <div>
                    <div className='font-medium'>Free Pre-Roll</div>
                    <div className='text-md text-muted-foreground'>
                      165 redemptions
                    </div>
                  </div>
                  <div className='font-medium'>$1,320</div>
                </div>
                <div className='flex items-center justify-between'>
                  <div>
                    <div className='font-medium'>30% Off Accessories</div>
                    <div className='text-md text-muted-foreground'>
                      142 redemptions
                    </div>
                  </div>
                  <div className='font-medium'>$2,130</div>
                </div>
                <div className='flex items-center justify-between'>
                  <div>
                    <div className='font-medium'>Loyalty Points Bonus</div>
                    <div className='text-md text-muted-foreground'>
                      128 redemptions
                    </div>
                  </div>
                  <div className='font-medium'>$960</div>
                </div>
                <div className='flex items-center justify-between'>
                  <div>
                    <div className='font-medium'>Free Delivery</div>
                    <div className='text-md text-muted-foreground'>
                      112 redemptions
                    </div>
                  </div>
                  <div className='font-medium'>$840</div>
                </div>
              </div>
            </ScrollArea>
          </CardContent>
        </Card>
      </div>

      <div className='col-span-1 md:col-span-1'>
        <Card className='h-full shadow-none'>
          <CardHeader>
            <CardTitle className='text-lg font-semibold'>
              Push Notifications Overview
            </CardTitle>
            <CardDescription className='text-md'>
              Track your push notification performance
            </CardDescription>
          </CardHeader>
          <CardContent className='space-y-6'>
            {/* Numeric metrics */}
            <div className='space-y-4'>
              <div>
                <div className='text-md text-muted-foreground'>
                  Total Push Notifications Delivered (this week)
                </div>
                <div className='text-2xl font-bold'>12,480</div>
              </div>
              <div>
                <div className='text-md text-muted-foreground'>
                  Push Campaigns (this week)
                </div>
                <div className='text-2xl font-bold'>7</div>
              </div>
            </div>

            {/* Divider */}
            <div className='h-px bg-border' aria-hidden='true'></div>

            {/* Progress metrics */}
            <div className='space-y-4'>
              <div>
                <div className='flex items-center justify-between mb-1.5'>
                  <div className='text-md text-muted-foreground'>
                    Wallet Opt-In Rate
                  </div>
                  <div className='text-md text-muted-foreground'>82%</div>
                </div>
                <div className='h-2 w-full bg-muted rounded-full overflow-hidden'>
                  <div
                    className='h-full bg-secondary rounded-full'
                    style={{ width: '82%' }}
                  ></div>
                </div>
              </div>
              <div>
                <div className='flex items-center justify-between mb-1.5'>
                  <div className='text-md text-muted-foreground'>
                    Push Delivery Success
                  </div>
                  <div className='text-md text-muted-foreground'>97%</div>
                </div>
                <div className='h-2 w-full bg-muted rounded-full overflow-hidden'>
                  <div
                    className='h-full bg-secondary rounded-full'
                    style={{ width: '97%' }}
                  ></div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {liveChatEnabled && (
        <div className='col-span-1 md:col-span-1'>
          <Card className='h-full shadow-none'>
            <CardHeader>
              <CardTitle className='text-lg font-semibold'>
                Live Chat Snapshot
              </CardTitle>
              <CardDescription className='text-md'>
                Track your customer service performance
              </CardDescription>
            </CardHeader>
            <CardContent className='space-y-6'>
              {/* First row with three metrics side by side */}
              <div className='grid grid-cols-3 gap-2 sm:gap-4'>
                <div className='text-center sm:text-left'>
                  <div className='text-sm sm:text-md text-muted-foreground truncate'>
                    Total Conversations
                  </div>
                  <div className='text-xl sm:text-2xl font-bold'>137</div>
                </div>
                <div className='text-center sm:text-left'>
                  <div className='text-sm sm:text-md text-muted-foreground truncate'>
                    Human Conversations
                  </div>
                  <div className='text-xl sm:text-2xl font-bold'>89</div>
                </div>
                <div className='text-center sm:text-left'>
                  <div className='text-sm sm:text-md text-muted-foreground truncate'>
                    AI Conversations
                  </div>
                  <div className='text-xl sm:text-2xl font-bold'>48</div>
                </div>
              </div>

              {/* Average Response Time */}
              <div className='text-center sm:text-left'>
                <div className='text-md text-muted-foreground'>
                  Avg Response Time
                </div>
                <div className='text-2xl font-bold'>1m 47s</div>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};
export default observer(HomeDevelopment);
